/* Consolidated CRM admin pages — Phase 2 additions */
import React, { useEffect, useState } from "react";
import { hasRole, isAdminLevel } from "../../lib/roles";
import { api, API_BASE } from "../../lib/api";
import BudgetMatcherBar from "../../components/BudgetMatcherBar";
import { toast } from "sonner";
import { Plus, X, Download, CheckCircle, AlertTriangle, MapPin, Loader2, MessageCircle } from "lucide-react";
import { fmtINR, waLink, storeLabel } from "../../lib/constants";
import Pagination, { usePagination, TableScroller } from "../../components/Pagination";
import { useAuth } from "../../lib/auth";
import ImageUploader from "../../components/ImageUploader";
import CameraCapture from "../../components/CameraCapture";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import Paginated from "../../components/Paginated";
import DateInput from "../../components/DateInput";
import WASendButton from "../../components/WASendButton";

const COLORS = ["#6B1A2C", "#C9A063", "#A4823F", "#D4AF37", "#8E2438"];

// ============ AUDIT LOG ============
export function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ entities: [], actions: [] });
  const [filter, setFilter] = useState({ entity: "", action: "", q: "" });
  const [qInput, setQInput] = useState("");

  useEffect(() => {
    api.get("/api/crm/audit-log/options").then((r) => setOptions(r.data || { entities: [], actions: [] })).catch(() => {});
  }, []);

  // Debounce the free-text search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setFilter((f) => ({ ...f, q: qInput })), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    const qp = new URLSearchParams();
    if (filter.entity) qp.set("entity", filter.entity);
    if (filter.action) qp.set("action", filter.action);
    if (filter.q) qp.set("q", filter.q);
    api.get(`/api/crm/audit-log?${qp}`).then((r) => setItems(r.data || []));
  }, [filter]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Trust</div><h1 className="font-display text-4xl text-maroon">Global Audit Log</h1></div>
      <div className="flex flex-wrap gap-3 mb-4">
        <select className="border border-line px-3 py-2 bg-white text-sm" value={filter.entity} onChange={(e) => setFilter({...filter, entity: e.target.value})} data-testid="audit-entity-filter">
          <option value="">All entities</option>
          {options.entities.map((en) => <option key={en} value={en}>{en}</option>)}
        </select>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={filter.action} onChange={(e) => setFilter({...filter, action: e.target.value})} data-testid="audit-action-filter">
          <option value="">All actions</option>
          {options.actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input placeholder="Search actor / entity / action / ID…" className="border border-line px-3 py-2 bg-white text-sm flex-1 min-w-[220px]" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="audit-search-input"/>
        {(filter.entity || filter.action || qInput) && (
          <button onClick={() => { setFilter({ entity: "", action: "", q: "" }); setQInput(""); }} className="text-xs text-maroon underline" data-testid="audit-clear-filters">Clear</button>
        )}
      </div>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["When","Actor","Role","Action","Entity","ID"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-line/60" data-testid={`audit-row-${it.id}`}>
                <td className="p-3 text-xs">{new Date(it.created_at).toLocaleString()}</td>
                <td className="p-3">{it.actor_name}</td>
                <td className="p-3 text-ash text-xs">{it.actor_role}</td>
                <td className="p-3"><span className="bg-gold/20 px-2 py-0.5 text-[10px] uppercase tracking-widest">{it.action}</span></td>
                <td className="p-3">{it.entity}</td>
                <td className="p-3 text-xs text-ash">{it.entity_id?.slice(0,12)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No audit entries.</div>}
      </div>
    </div>
  );
}

// ============ ATTENDANCE WITH SELFIE ============
export function AttendancePage() {
  const [items, setItems] = useState([]);
  const [selfie, setSelfie] = useState([]);
  const [location, setLocation] = useState("");
  const [coords, setCoords] = useState(null);   // { lat, lng, acc }
  const [geoBusy, setGeoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/api/crm/attendance/mine").then((r) => setItems(r.data || []));

  const captureGPS = (silent = false) => {
    if (!navigator.geolocation) { if (!silent) toast.error("GPS isn't supported on this device"); return; }
    if (!window.isSecureContext) { if (!silent) toast.error("Location needs a secure (https) connection"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const { latitude, longitude, accuracy } = p.coords;
        setCoords({ lat: latitude, lng: longitude, acc: accuracy });
        setLocation(`${latitude.toFixed(6)},${longitude.toFixed(6)}`);
        setGeoBusy(false);
        if (!silent) toast.success(`Location locked (±${Math.round(accuracy)}m)`);
      },
      (err) => {
        setGeoBusy(false);
        if (silent) return;
        const msg =
          err.code === 1 ? "Location permission denied — allow it in your browser settings and retry"
          : err.code === 2 ? "Location unavailable — turn on GPS / location services and retry"
          : err.code === 3 ? "Location timed out — move to an open area and retry"
          : "Could not get your location";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  // On open: load punches and try to grab location silently (no error toast if
  // the user hasn't granted permission yet — they can tap the button).
  useEffect(() => { load(); captureGPS(true); /* eslint-disable-next-line */ }, []);

  const punch = async (kind = "in") => {
    if (!selfie[0]) { toast.error("Take or upload a selfie first"); return; }
    setBusy(true);
    try {
      await api.post("/api/crm/attendance/punch", { selfie_url: selfie[0] || "", location, kind });
      toast.success(kind === "out" ? "Punched out" : "Punched in");
      setSelfie([]); setLocation(""); setCoords(null); load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="eyebrow">Daily</div>
        <h1 className="font-display text-3xl text-maroon">Punch In</h1>
        <div className="bg-white border border-line p-5 mt-4 space-y-4">
          <div>
            <div className="eyebrow text-[10px] mb-2">Selfie — take one now</div>
            <CameraCapture onCapture={(url) => setSelfie([url])} kind="attendance" />
          </div>

          <div>
            <div className="eyebrow text-[10px] mb-2">…or upload a photo</div>
            <ImageUploader value={selfie} onChange={setSelfie} multiple={false} kind="attendance" maxFiles={1} />
          </div>

          <div>
            <div className="eyebrow text-[10px] mb-2">Location (GPS)</div>
            <div className="flex gap-2 items-center">
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="lat,lng or address" className="flex-1 border border-line px-2 py-1.5 bg-pearl text-sm" data-testid="att-location" />
              <button onClick={() => captureGPS(false)} disabled={geoBusy} className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="att-gps">
                {geoBusy ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
                {geoBusy ? "Locating…" : "Use GPS"}
              </button>
            </div>
            {coords && (
              <div className="text-[11px] text-ash mt-1.5">
                Accuracy ±{Math.round(coords.acc)}m ·{" "}
                <a href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`} target="_blank" rel="noreferrer" className="text-maroon underline">View on map</a>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => punch("in")} disabled={busy || !selfie[0]} className="btn-maroon w-full justify-center" data-testid="att-punch">{busy ? "..." : "Punch In"}</button>
            <button onClick={() => punch("out")} disabled={busy || !selfie[0]} className="btn-outline w-full justify-center" data-testid="att-punch-out">{busy ? "..." : "Punch Out"}</button>
          </div>
          <p className="text-[10px] text-ash text-center">Both punch-in and punch-out need a fresh selfie and GPS.</p>
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="eyebrow mb-3">My recent punches</div>
        <div className="space-y-2">
          {items.map((it) => {
            const isCoords = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(it.location || "");
            return (
              <div key={it.id} className="bg-white border border-line p-3 flex items-center gap-3" data-testid={`att-${it.id}`}>
                {it.selfie_url && <img src={it.selfie_url} alt="" className="w-12 h-12 object-cover rounded" />}
                <div className="flex-1">
                  <div className="text-sm flex items-center gap-2">
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${it.kind === "out" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{it.kind === "out" ? "Out" : "In"}</span>
                    {new Date(it.punched_at).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-ash">
                    {isCoords
                      ? <a href={`https://www.google.com/maps?q=${it.location}`} target="_blank" rel="noreferrer" className="text-maroon underline">{it.location} · map</a>
                      : (it.location || "—")}
                  </div>
                </div>
                <CheckCircle size={16} className="text-green-700" />
              </div>
            );
          })}
          {items.length === 0 && <div className="text-center text-xs text-ash py-10">No punches yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ ATTENDANCE — ADMIN VIEW (all staff punches) ============
export function AttendanceAdminPage() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("salesperson");   // default to the sales team
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [selUser, setSelUser] = useState("");
  const [stats, setStats] = useState(null);
  const [leaveMode, setLeaveMode] = useState("leave"); // "leave" | "half"

  useEffect(() => { api.get("/api/crm/attendance").then((r) => setItems(r.data || [])).catch(() => {}); }, []);

  // Distinct staff seen in the punch log, for the statistics dropdown.
  const people = Array.from(new Map(items.map((it) => [it.user_id, it.user_name])).entries())
    .map(([id, name]) => ({ id, name })).filter((p) => p.id);

  useEffect(() => {
    if (!selUser) { setStats(null); return; }
    api.get(`/api/crm/attendance/stats?user_id=${selUser}`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [selUser]);

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/api/crm/attendance/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "attendance.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Could not export"); }
  };

  const filtered = items.filter((it) => {
    if (role && (it.user_role || "") !== role) return false;
    if (q && !((it.user_name || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const isCoords = (s) => /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(s || "");
  const ROLES = ["salesperson", "admin", "major_admin", "karigar", "astrologer", "catalogue_admin"];
  const StatBar = ({ label, s }) => (
    <div data-testid={`att-stat-${label}`}>
      <div className="flex justify-between text-[11px] mb-1"><span className="text-ash">{label}</span><span className="font-medium text-maroon">{s?.pct ?? 0}%</span></div>
      <div className="h-3 bg-line rounded-full overflow-hidden"><div className="h-full bg-maroon" style={{ width: `${Math.min(s?.pct || 0, 100)}%` }}/></div>
      <div className="text-[9px] text-ash mt-0.5">{s?.present ?? 0}/{s?.total ?? 0} days</div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="eyebrow">Staff</div>
          <h1 className="font-display text-4xl text-maroon">Attendance Records</h1>
          <p className="text-xs text-ash mt-1">Punch-in / punch-out with selfie and GPS. Filter, view per-person statistics, or export.</p>
        </div>
        <button onClick={downloadCsv} className="btn-outline text-sm inline-flex items-center gap-1" data-testid="att-export"><Download size={14}/> Download CSV</button>
      </div>

      {/* Per-person statistics */}
      <div className="bg-white border border-line p-5 mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="eyebrow">Statistics</div>
          <select className="border border-line px-3 py-2 bg-white text-sm" value={selUser} onChange={(e) => setSelUser(e.target.value)} data-testid="att-stat-person">
            <option value="">Choose a staff member…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {stats?.user?.role && <span className="text-xs text-ash">{(stats.user.role || "").replace("_", " ")}</span>}
        </div>
        {!stats ? (
          <div className="text-xs text-ash py-6 text-center">Pick a staff member to see their attendance percentage for this week, month, year and all-time.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatBar label="This week" s={stats.stats?.week}/>
              <StatBar label="This month" s={stats.stats?.month}/>
              <StatBar label="This year" s={stats.stats?.year}/>
              <StatBar label="All-time" s={stats.stats?.all}/>
            </div>
            {/* Leave / half-day toggle */}
            <div className="mt-5">
              <div className="inline-flex border border-line rounded overflow-hidden text-xs mb-2">
                <button onClick={() => setLeaveMode("leave")} className={`px-3 py-1.5 ${leaveMode === "leave" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="att-toggle-leave">Leaves</button>
                <button onClick={() => setLeaveMode("half")} className={`px-3 py-1.5 ${leaveMode === "half" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="att-toggle-half">Half-days</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(leaveMode === "leave" ? stats.leaves : stats.half_days).map((lv, i) => (
                  <span key={i} className="text-[11px] bg-pearl border border-line px-2 py-1 rounded" data-testid={`att-${leaveMode}-${i}`}>
                    {lv.from}{lv.to && lv.to !== lv.from ? ` → ${lv.to}` : ""}{lv.reason ? ` · ${lv.reason}` : ""}
                  </span>
                ))}
                {(leaveMode === "leave" ? stats.leaves : stats.half_days).length === 0 && <span className="text-xs text-ash">None recorded.</span>}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select className="border border-line px-3 py-2 bg-white text-sm" value={role} onChange={(e) => setRole(e.target.value)} data-testid="att-role-filter">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
        </select>
        <input placeholder="Search staff name…" className="border border-line px-3 py-2 bg-white text-sm flex-1 min-w-[200px]" value={q} onChange={(e) => setQ(e.target.value)} data-testid="att-name-search"/>
        <span className="text-xs text-ash self-center">{filtered.length} record{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["Selfie", "Staff", "Role", "In/Out", "When", "Location"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-b border-line/60" data-testid={`att-admin-${it.id}`}>
                <td className="p-2">
                  {it.selfie_url
                    ? <img src={it.selfie_url} alt="" className="w-12 h-12 object-cover rounded cursor-pointer" onClick={() => setPreview(it.selfie_url)}/>
                    : <span className="text-ash text-xs">—</span>}
                </td>
                <td className="p-3">{it.user_name}</td>
                <td className="p-3 text-ash text-xs">{(it.user_role || "").replace("_", " ")}</td>
                <td className="p-3"><span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${it.kind === "out" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{it.kind === "out" ? "Out" : "In"}</span></td>
                <td className="p-3 text-xs">{new Date(it.punched_at).toLocaleString()}</td>
                <td className="p-3 text-xs">
                  {isCoords(it.location)
                    ? <a href={`https://www.google.com/maps?q=${it.location}`} target="_blank" rel="noreferrer" className="text-maroon underline">{it.location} · map</a>
                    : (it.location || "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-12 text-center text-ash text-sm">No attendance records for this filter.</div>}
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="selfie" className="max-h-[85vh] max-w-full rounded"/>
        </div>
      )}
    </div>
  );
}

// ============ SOLD PRODUCTS (super/major/admin) ============
// One row per PIECE sold — if a customer bought four, that is four rows, because
// each physical piece carries its own barcode. Every row names the barcode, the
// customer who took it and the staff member who sold or assisted on it.
export function SoldProductsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/api/crm/sold-products")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Sold by mistake, or the barcode came back to the counter: put the piece
  // back into stock. It leaves this list and becomes sellable again.
  const restore = async (r) => {
    if (!r.barcode) { toast.error("This line has no barcode to put back"); return; }
    if (!window.confirm(`Put ${r.barcode} back into stock? It will be removed from Sold Products and become available to sell again.`)) return;
    try {
      await api.post("/api/crm/inventory/restore", { label_no: r.barcode, reason: "Reversed from Sold Products" });
      toast.success(`${r.barcode} is back in stock`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not put it back"); }
  };

  const filtered = rows.filter((r) => {
    if (channel && r.channel !== channel) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return [r.name, r.barcode, r.customer_name, r.customer_phone, r.sold_by, r.huid, r.reference]
      .some((f) => (f || "").toLowerCase().includes(needle));
  });
  const totalUnits = filtered.length;                       // one row = one piece
  const totalRevenue = filtered.reduce((a, r) => a + (r.price || 0), 0);
  const distinct = new Set(filtered.map((r) => r.product_id)).size;

  const exportCsv = () => {
    const head = ["Sold At", "Barcode", "Product", "Category", "Metal", "Purity", "Weight",
                  "HUID Charges", "Price", "Customer", "Phone", "Sold By", "Role", "Channel", "Reference"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [head.join(",")].concat(filtered.map((r) => [
      r.sold_at || "", r.barcode || "", r.name || "", r.category || "", r.metal || "",
      r.purity || "", r.weight_grams ?? "", r.huid_charges ?? "", r.price ?? "",
      r.customer_name || "", r.customer_phone || "", r.sold_by || "", r.sold_by_role || "",
      r.channel || "", r.reference || "",
    ].map(esc).join(","))).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "sold-products.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Sales</div>
        <h1 className="font-display text-4xl text-maroon">Sold Products</h1>
        <p className="text-xs text-ash mt-1">
          Every piece sold, one line each — with its barcode, who bought it and who sold it.
          A customer buying four pieces shows as four separate lines.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Pieces sold</div><div className="font-display text-2xl text-maroon">{totalUnits}</div></div>
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Distinct designs</div><div className="font-display text-2xl text-maroon">{distinct}</div></div>
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Total revenue</div><div className="font-display text-2xl text-maroon">{fmtINR(totalRevenue)}</div></div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search barcode, product, customer, salesperson…" className="border border-line px-3 py-2 bg-white text-sm w-full sm:w-96" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sold-search"/>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="sold-channel">
          <option value="">All channels</option>
          <option value="Counter (slip)">Counter (slip)</option>
          <option value="Online order">Online order</option>
          <option value="Stock file (offline sale)">Stock file (offline sale)</option>
        </select>
        <button onClick={exportCsv} className="btn-outline text-sm" data-testid="sold-export">Export CSV</button>
      </div>

      {loading ? (
        <div className="text-center text-ash py-16">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-ash py-16">No pieces have been sold yet.</div>
      ) : (
        <Paginated items={filtered} pageSize={25} label="pieces" scroller>
          {(page) => (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line"><tr>{[
                  "Image", "Barcode", "Product", "Weight", "Purity", "Unit Sold",
                  "Price", "Customer", "Sold By", "Channel", "Sold On", "",
                ].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>)}</tr></thead>
                <tbody>
                  {page.map((r) => (
                    <tr key={r.row_id} className="border-b border-line/60" data-testid={`sold-${r.row_id}`}>
                      <td className="p-2">
                        {r.images?.[0]
                          ? <img src={r.images[0]} alt="" className="w-12 h-12 object-cover rounded cursor-pointer" onClick={() => setPreview(r.images)} title="Click to enlarge"/>
                          : <span className="text-ash text-xs">—</span>}
                      </td>
                      <td className="p-3 font-mono text-xs text-maroon whitespace-nowrap">{r.barcode || "—"}</td>
                      <td className="p-3">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[10px] text-ash">{[r.category, r.size && `Size ${r.size}`, r.huid && `HUID ${r.huid}`].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="p-3 text-xs whitespace-nowrap">{r.metal === "Gemstone" ? `${r.weight_grams || 0} ct` : `${r.weight_grams ?? "—"}g`}</td>
                      <td className="p-3 text-xs">{r.metal === "Gemstone" ? "—" : (r.purity || "—")}</td>
                      {/* Always 1 — each line is a single physical piece. */}
                      <td className="p-3 font-semibold">{r.units_sold}</td>
                      <td className="p-3 text-maroon font-semibold whitespace-nowrap">
                        {fmtINR(r.price)}
                        {r.huid_charges > 0 && <div className="text-[9px] text-ash font-normal">incl. HUID {fmtINR(r.huid_charges)}</div>}
                      </td>
                      <td className="p-3 text-xs">
                        <div>{r.customer_name || "—"}</div>
                        <div className="text-[10px] text-ash">{r.customer_phone || ""}</div>
                      </td>
                      <td className="p-3 text-xs">
                        <div>{r.sold_by || "—"}</div>
                        <div className="text-[10px] text-ash capitalize">{r.sold_by_role || ""}</div>
                      </td>
                      <td className="p-3 text-[10px]">
                        <span className={`px-1.5 py-0.5 rounded ${r.channel === "Online order" ? "bg-blue-50 text-blue-700" : "bg-gold/20 text-maroon"}`}>{r.channel}</span>
                        <div className="text-ash mt-0.5">{r.reference}</div>
                      </td>
                      <td className="p-3 text-xs whitespace-nowrap">{r.sold_at ? new Date(r.sold_at).toLocaleDateString() : "—"}</td>
                      <td className="p-3">
                        {r.barcode && (
                          <button onClick={() => restore(r)} className="text-[11px] text-maroon underline whitespace-nowrap" data-testid={`restore-${r.barcode}`}>
                            Put back
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Paginated>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6 gap-3 flex-wrap" onClick={() => setPreview(null)}>
          {preview.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" className="max-h-[70vh] max-w-[45vw] rounded"/>)}
        </div>
      )}
    </div>
  );
}

// ============ SWAYAMBHU GOLD SCHEME (super/major/admin + salesperson leads) ============
const GS_PIE = ["#7a1f2b", "#c9a227", "#3f7a4f", "#b0743b"];

// Per-member helpers: months paid, pending, last payment, whether paid THIS month.
function gsDerive(e) {
  const paid = e.installments_paid || 0;
  const total = e.duration_months || 12;
  const inst = e.installments || [];
  const last = inst.length ? inst[inst.length - 1] : null;
  const lastAt = last ? new Date(last.paid_at) : null;
  const now = new Date();
  const paidThisMonth = lastAt && lastAt.getFullYear() === now.getFullYear() && lastAt.getMonth() === now.getMonth();
  return { paid, total, pending: Math.max(total - paid, 0), lastAt, lastRate: last?.rate_per_gram || 0, paidThisMonth };
}

function gsWhatsAppMessage(e, { salesperson } = {}) {
  const d = gsDerive(e);
  const lines = [];
  if (salesperson) {
    lines.push(`Hi ${e.customer_name || "there"}, ${salesperson} here from Swayambhu Gold.`);
    lines.push(`Your Swayambhu Gold Scheme update: you've paid ${d.paid} of ${d.total} months, ${d.pending} remaining.`);
  } else {
    lines.push(`Hi ${e.customer_name || "there"}, this is Swayambhu Gold.`);
    lines.push(`Your Swayambhu Gold Scheme: ${d.paid} of ${d.total} months paid, ${d.pending} remaining.`);
  }
  lines.push(`Amount paid so far: ₹${Math.round(e.total_paid || 0).toLocaleString("en-IN")}. Gold accumulated: ${(e.total_grams || 0).toFixed(3)} g.`);
  if (d.lastRate) lines.push(`Last rate/gram locked: ₹${Math.round(d.lastRate).toLocaleString("en-IN")}.`);
  if (e.status !== "completed") lines.push(`Please pay this month's ₹${Math.round(e.monthly_amount || 0).toLocaleString("en-IN")} to keep your scheme active.`);
  else lines.push(`Your scheme is complete — visit us to get your ornament made with free making charges!`);
  return lines.join(" ");
}

export function GoldSchemeAdminPage() {
  const { user } = useAuth();
  const isSalespersonView = hasRole(user, "salesperson") && !isAdminLevel(user);
  const [data, setData] = useState({ enrollments: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [slice, setSlice] = useState("all"); // "all" | "pending" | "approaching" | "completed" | month number as string

  const url = isSalespersonView ? "/api/crm/gold-scheme/my-leads" : "/api/crm/gold-scheme/enrollments";
  const load = () => api.get(url).then((r) => setData(r.data || { enrollments: [], summary: {} })).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordPayment = async (id) => {
    if (!window.confirm("Record an in-store instalment at today's gold rate?")) return;
    try { await api.post(`/api/crm/gold-scheme/${id}/installment`, {}); toast.success("Instalment recorded — gold credited"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const all = data.enrollments || [];
  const s = data.summary || {};

  // Chart data: members by months-paid (0..12)
  const byMonth = Array.from({ length: 13 }, (_, m) => ({ month: m, members: all.filter((e) => (e.installments_paid || 0) === m).length }));
  const statusPie = [
    { name: "Active", value: all.filter((e) => e.status === "active").length },
    { name: "Completed", value: all.filter((e) => e.status === "completed").length },
  ].filter((d) => d.value > 0);
  const pendingThisMonth = all.filter((e) => e.status === "active" && !gsDerive(e).paidThisMonth);
  const approaching = all.filter((e) => e.status === "active" && (e.installments_paid || 0) >= 10);

  // Slicer
  const rows = all.filter((e) => {
    if (q && !((e.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (e.customer_phone || "").includes(q))) return false;
    if (slice === "pending") return e.status === "active" && !gsDerive(e).paidThisMonth;
    if (slice === "approaching") return e.status === "active" && (e.installments_paid || 0) >= 10;
    if (slice === "completed") return e.status === "completed";
    if (/^\d+$/.test(slice)) return (e.installments_paid || 0) === +slice;
    return true;
  });

  const waFor = (e) => waLink(gsWhatsAppMessage(e, { salesperson: (isSalespersonView && e.salesperson_name) ? e.salesperson_name : null }), e.customer_phone);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Schemes</div>
        <h1 className="font-display text-4xl text-maroon">{isSalespersonView ? "My Gold Scheme Leads" : "Swayambhu Gold Scheme"}</h1>
        <p className="text-xs text-ash mt-1">{isSalespersonView ? "Customers who enrolled through you — track and remind them." : "Members, payments, gold accumulated, and reminders."}</p>
      </div>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-5 gap-3 mb-6">
        <button onClick={() => setSlice("all")} className={`bg-white border p-3 text-left ${slice === "all" ? "border-maroon" : "border-line"}`}><div className="eyebrow text-[10px]">Members</div><div className="font-display text-2xl text-maroon">{s.members || 0}</div></button>
        <div className="bg-white border border-line p-3"><div className="eyebrow text-[10px]">Active</div><div className="font-display text-2xl text-maroon">{s.active || 0}</div></div>
        <button onClick={() => setSlice("completed")} className={`bg-white border p-3 text-left ${slice === "completed" ? "border-maroon" : "border-line"}`}><div className="eyebrow text-[10px]">Completed</div><div className="font-display text-2xl text-maroon">{s.completed || 0}</div></button>
        <button onClick={() => setSlice("pending")} className={`bg-white border p-3 text-left ${slice === "pending" ? "border-maroon" : "border-line"}`}><div className="eyebrow text-[10px]">Not paid this month</div><div className="font-display text-2xl text-red-700">{pendingThisMonth.length}</div></button>
        <button onClick={() => setSlice("approaching")} className={`bg-white border p-3 text-left ${slice === "approaching" ? "border-maroon" : "border-line"}`}><div className="eyebrow text-[10px]">Approaching 1 year</div><div className="font-display text-2xl text-maroon">{approaching.length}</div></button>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-line p-4 lg:col-span-2">
          <div className="eyebrow text-[10px] mb-2">Members by months paid <span className="text-ash normal-case tracking-normal">(click a bar to filter)</span></div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={byMonth} onClick={(st) => st?.activeLabel != null && setSlice(String(st.activeLabel))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
              <XAxis dataKey="month" tick={{ fontSize: 11 }}/><YAxis allowDecimals={false} tick={{ fontSize: 11 }}/>
              <Tooltip/><Bar dataKey="members" fill="#7a1f2b" radius={[4, 4, 0, 0]} cursor="pointer"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-line p-4">
          <div className="eyebrow text-[10px] mb-2">Status</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                {statusPie.map((e, i) => <Cell key={i} fill={GS_PIE[i % GS_PIE.length]}/>)}
              </Pie>
              <Tooltip/><Legend/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input placeholder="Search member name or phone…" className="border border-line px-3 py-2 bg-white text-sm w-full sm:w-72" value={q} onChange={(e) => setQ(e.target.value)} data-testid="gs-search"/>
        {slice !== "all" && <button onClick={() => setSlice("all")} className="text-xs text-maroon underline">Clear filter ({slice})</button>}
      </div>

      {loading ? <div className="text-center text-ash py-16">Loading…</div> : rows.length === 0 ? (
        <div className="text-center text-ash py-16">No members for this view.</div>
      ) : (
        <div className="bg-white border border-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line"><tr>{["Member", "Phone", "Monthly", "Paid", "Gold (g)", "Progress", isSalespersonView ? "" : "Via", "Status", ""].filter((h) => h !== undefined).map((h, i) => <th key={i} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((e) => {
                const d = gsDerive(e);
                return (
                  <React.Fragment key={e.id}>
                    <tr className={`border-b border-line/60 ${d.paidThisMonth || e.status === "completed" ? "" : "bg-red-50/40"}`} data-testid={`gs-${e.id}`}>
                      <td className="p-3">{e.customer_name}{e.nominee_name ? <div className="text-[10px] text-ash">Nominee: {e.nominee_name}</div> : null}</td>
                      <td className="p-3 text-xs">{e.customer_phone}</td>
                      <td className="p-3">{fmtINR(e.monthly_amount)}</td>
                      <td className="p-3">{fmtINR(e.total_paid || 0)}</td>
                      <td className="p-3">{(e.total_grams || 0).toFixed(3)}</td>
                      <td className="p-3 text-xs">{d.paid}/{d.total} <span className="text-ash">({d.pending} left)</span></td>
                      {!isSalespersonView && <td className="p-3 text-xs">{e.salesperson_name || "—"}</td>}
                      <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${e.status === "completed" ? "bg-green-100 text-green-800" : "bg-gold/25 text-maroon"}`}>{e.status}</span></td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <a href={waFor(e)} target="_blank" rel="noreferrer" className="text-xs text-green-700 underline mr-3" data-testid={`gs-wa-${e.id}`}>WhatsApp</a>
                        <button onClick={() => setOpen(open === e.id ? null : e.id)} className="text-xs text-maroon underline mr-3">{open === e.id ? "Hide" : "Ledger"}</button>
                        {!isSalespersonView && e.status === "active" && <button onClick={() => recordPayment(e.id)} className="text-xs text-green-700 underline" data-testid={`gs-record-${e.id}`}>+ Instalment</button>}
                      </td>
                    </tr>
                    {open === e.id && (
                      <tr className="bg-pearl/40"><td colSpan={9} className="p-3">
                        {(e.installments || []).length === 0 ? <div className="text-xs text-ash">No instalments yet.</div> : (
                          <table className="w-full text-xs">
                            <thead><tr>{["#", "Paid on", "Amount", "Rate/g", "Gold (g)", "Channel", "By"].map((h) => <th key={h} className="text-left p-1.5 eyebrow text-[9px]">{h}</th>)}</tr></thead>
                            <tbody>
                              {e.installments.map((it) => (
                                <tr key={it.payment_id}>
                                  <td className="p-1.5">{it.month_no}</td><td className="p-1.5">{new Date(it.paid_at).toLocaleString()}</td>
                                  <td className="p-1.5">{fmtINR(it.amount)}</td><td className="p-1.5">{fmtINR(it.rate_per_gram)}</td>
                                  <td className="p-1.5">{(it.grams || 0).toFixed(3)}</td><td className="p-1.5">{(it.channel || "").replace("_", " ")}</td><td className="p-1.5">{it.marked_by}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ REFERRALS (super/major admin) ============
export function ReferralsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/api/crm/referrals").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const thankMsg = (g, p) => {
    const date = p.referred_at ? new Date(p.referred_at).toLocaleDateString("en-GB") : "recently";
    return `Hi ${g.referrer_name || "there"}, thank you for referring ${p.name} (${p.phone}) to Swayambhu Gold. ${p.name} visited us on ${date}. Keep referring — we appreciate you!`;
  };
  const sendThanks = async (g, p) => {
    try { await api.post(`/api/crm/referrals/${p.id}/thanked`); } catch { /* still open WA */ }
    window.open(waLink(thankMsg(g, p), g.referrer_phone), "_blank");
    load();
  };

  const money = (v) => v > 0 ? fmtINR(v) : "—";

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">CRM</div>
        <h1 className="font-display text-4xl text-maroon">Referrals</h1>
        <p className="text-xs text-ash mt-1">Who referred whom, what the referred customer bought on their visit day, and a one-tap WhatsApp thank-you. Freshest / unsent at the top.</p>
      </div>

      {loading ? <div className="text-center text-ash py-16">Loading…</div> : rows.length === 0 ? (
        <div className="text-center text-ash py-16">No referrals yet. They appear when a salesperson records a referral while adding a customer.</div>
      ) : (
        <div className="space-y-4">
          {rows.map((g, i) => (
            <div key={g.referrer_id} className="bg-white border border-line overflow-hidden" data-testid={`ref-group-${g.referrer_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 p-4 bg-pearl border-b border-line">
                <div className="flex items-center gap-3">
                  <span className="font-display text-2xl text-maroon w-8">{i + 1}</span>
                  <div>
                    <div className="font-medium">{g.referrer_name || "—"} <span className="text-xs text-ash">· {g.referrer_phone}</span></div>
                    <div className="text-[11px] text-ash">Code {g.referrer_code || "—"} · referred <b>{g.count}</b> {g.count === 1 ? "person" : "people"}</div>
                  </div>
                </div>
                {g.has_unsent && <span className="text-[10px] uppercase tracking-widest bg-gold/30 text-maroon px-2 py-0.5">Thank-you pending</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-line"><tr>{["Referred customer", "Visited", "Gold", "Silver", "Gemstone", "Custom", "Thank-you"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
                  <tbody>
                    {g.referred.map((p) => (
                      <tr key={p.id} className={`border-b border-line/60 ${p.thanked_at ? "" : "bg-gold/5"}`} data-testid={`ref-row-${p.id}`}>
                        <td className="p-3">{p.name}<div className="text-[10px] text-ash">{p.phone}</div></td>
                        <td className="p-3 text-xs">{p.referred_at ? new Date(p.referred_at).toLocaleDateString("en-GB") : "—"}</td>
                        <td className="p-3 text-xs">{money(p.purchases?.gold)}</td>
                        <td className="p-3 text-xs">{money(p.purchases?.silver)}</td>
                        <td className="p-3 text-xs">{money(p.purchases?.gemstone)}</td>
                        <td className="p-3 text-xs">{money(p.purchases?.custom)}</td>
                        <td className="p-3">
                          <button onClick={() => sendThanks(g, p)} className={`inline-flex items-center gap-1 text-xs underline ${p.thanked_at ? "text-ash" : "text-green-700"}`} data-testid={`ref-wa-${p.id}`}>
                            <MessageCircle size={13}/> {p.thanked_at ? "Send again" : "WhatsApp"}
                          </button>
                          {p.thanked_at && <div className="text-[9px] text-ash mt-0.5">Sent {new Date(p.thanked_at).toLocaleDateString("en-GB")} {new Date(p.thanked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ CUSTOMISATION INPUTS (super/major/admin) ============
const CI_CATEGORIES = ["Necklace", "Mangalsutra", "Rings", "Earrings", "Chains", "Bangles", "Pendant", "Anklets", "Diamond", "Precious Stone", "Semi-precious Stone"];
const CI_PURITIES = ["18K", "22K", "24K", "Silver", "—"];
const CI_EMPTY = { category: "Pendant", metal: "Gold", purity: "22K", weight_grams: 0, images: [], name: "", details: "", slot: 1, active: true };

export function CustomisationInputsPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(CI_EMPTY);
  const [images, setImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/api/crm/customisation-inputs").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(CI_EMPTY); setImages([]); setShowForm(true); };
  const openEdit = (r) => { setEditingId(r.id); setForm({ ...CI_EMPTY, ...r }); setImages(r.images || []); setShowForm(true); };

  const save = async () => {
    if (images.length === 0) { toast.error("Add at least one image"); return; }
    const payload = { ...form, images, weight_grams: +form.weight_grams || 0, slot: +form.slot || 1 };
    try {
      if (editingId) { await api.patch(`/api/crm/customisation-inputs/${editingId}`, payload); toast.success("Design updated"); }
      else { await api.post("/api/crm/customisation-inputs", payload); toast.success("Design added"); }
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this customisation design?")) return;
    try { await api.delete(`/api/crm/customisation-inputs/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow">Customisation</div>
          <h1 className="font-display text-4xl text-maroon">Customisation Designs</h1>
          <p className="text-xs text-ash mt-1">These appear as the second row of 4 designs in the customer's "Customise" panel, matched by category. Every change is logged in the Audit Log.</p>
        </div>
        <button onClick={openCreate} className="btn-maroon" data-testid="ci-add"><Plus size={14}/> Add Design</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-line overflow-hidden" data-testid={`ci-${r.id}`}>
            <div className="aspect-video bg-pearl overflow-hidden flex">
              {(r.images || []).slice(0, 3).map((img, i) => <img key={i} src={img} alt="" className="h-full flex-1 object-cover"/>)}
            </div>
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{r.name || r.category}</div>
                <span className="text-[9px] uppercase tracking-widest bg-gold/25 text-maroon px-1.5 py-0.5">Slot {r.slot}</span>
              </div>
              <div className="text-[11px] text-ash mt-0.5">{r.category} · {r.metal} · {r.purity} · {r.weight_grams}g {r.active ? "" : "· (hidden)"}</div>
              {r.details && <div className="text-[11px] text-ash mt-1 line-clamp-2">{r.details}</div>}
              <div className="text-[9px] text-ash mt-2">By {r.updated_by || r.created_by}</div>
              <div className="flex gap-3 mt-2">
                <button onClick={() => openEdit(r)} className="text-xs text-maroon underline">Edit</button>
                <button onClick={() => remove(r)} className="text-xs text-red-600 underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-ash text-sm">No customisation designs yet. Add up to 4 per category.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white border border-line w-full max-w-lg max-h-[88vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="ci-form">
            <div className="flex items-center justify-between mb-3"><div className="font-display text-2xl text-maroon">{editingId ? "Edit" : "Add"} design</div><button onClick={() => setShowForm(false)}><X size={18}/></button></div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Category</span>
                <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CI_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Slot (1-4)</span>
                <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.slot} onChange={(e) => setForm({ ...form, slot: +e.target.value })}>{[1, 2, 3, 4].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Metal</span>
                <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.metal} onChange={(e) => setForm({ ...form, metal: e.target.value })}><option>Gold</option><option>Silver</option><option>Gemstone</option></select></label>
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Purity</span>
                <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.purity} onChange={(e) => setForm({ ...form, purity: e.target.value })}>{CI_PURITIES.map((p) => <option key={p}>{p}</option>)}</select></label>
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Weight (g)</span>
                <input type="number" step="0.01" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })}/></label>
              <label className="block text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Design name</span>
                <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Peacock pendant"/></label>
            </div>
            <textarea rows={2} placeholder="Other details (stones, style…)" className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-3" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}/>
            <div className="mb-3">
              <span className="text-[10px] text-ash uppercase tracking-widest block mb-1">Images (one product can have multiple)</span>
              <ImageUploader value={images} onChange={setImages} multiple maxFiles={3} kind="customisation"/>
            </div>
            <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}/> Show to customers</label>
            <button onClick={save} className="btn-maroon w-full justify-center" data-testid="ci-save">{editingId ? "Save changes" : "Add design"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ TARGETS ============
const PERIOD_TYPES = [
  { key: "day", label: "For a Day" },
  { key: "week", label: "For a Week" },
  { key: "month", label: "For a Month" },
  { key: "year", label: "For a Year" },
];

const currentPeriodOf = (type) => {
  const now = new Date();
  if (type === "day") return now.toISOString().slice(0, 10);
  if (type === "week") {
    // ISO week string YYYY-Www
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
  }
  if (type === "year") return String(now.getFullYear());
  return now.toISOString().slice(0, 7); // month
};

const periodLabel = (t) => {
  const pt = t.period_type || "month";
  const tag = { day: "Daily", week: "Weekly", month: "Monthly", year: "Yearly" }[pt];
  return `${tag} · ${t.period}`;
};

export function TargetsPage() {
  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ user_id: "", period_type: "month", period: currentPeriodOf("month"), target_amount: 100000, notes: "" });
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();
  const canAllot = hasRole(user, ["super_admin", "major_admin", "admin"]);
  const load = () => api.get("/api/crm/targets").then((r) => setTargets(r.data || []));
  useEffect(() => {
    load();
    if (canAllot) api.get("/api/crm/users?role=salesperson").then((r) => setUsers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/api/crm/targets", form); toast.success("Target assigned"); setShowForm(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setType = (period_type) => setForm({ ...form, period_type, period: currentPeriodOf(period_type) });
  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Day · Week · Month · Year</div><h1 className="font-display text-4xl text-maroon">Sales Targets</h1></div>
        {canAllot && <button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-target-btn"><Plus size={14}/> Set Target</button>}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {targets.map((t) => (
          <div key={t.id} className="bg-white border border-line p-5" data-testid={`target-${t.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow text-[10px]">{periodLabel(t)}</div>
                <div className="font-medium">{users.find(u => u.id === t.user_id)?.name || t.user_id?.slice(0,8)}</div>
                {t.created_by_name && <div className="text-[10px] text-ash">Assigned by {t.created_by_name}</div>}
              </div>
              <div className="text-right">
                <div className="text-sm text-ash">Target</div>
                <div className="font-display text-2xl text-maroon">{fmtINR(t.target_amount)}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-ash"><span>Achieved {fmtINR(t.achieved || 0)}</span><span>{t.achievement_pct}%</span></div>
              <div className="h-2 bg-line mt-1 overflow-hidden"><div className="h-full bg-gradient-to-r from-gold to-maroon" style={{width: `${Math.min(t.achievement_pct, 100)}%`}}/></div>
            </div>
          </div>
        ))}
        {targets.length === 0 && <div className="text-center text-ash p-10 col-span-2">No targets set.</div>}
      </div>
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <div className="font-display text-2xl text-maroon">Set Target</div>
            <select required className="w-full border border-line px-3 py-2 bg-pearl" value={form.user_id} onChange={(e) => setForm({...form, user_id: e.target.value})} data-testid="target-user">
              <option value="">Select Salesperson</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <div>
              <span className="eyebrow text-[10px]">Target period</span>
              <div className="flex border border-line overflow-hidden mt-1">
                {PERIOD_TYPES.map((p) => (
                  <button key={p.key} type="button" onClick={() => setType(p.key)}
                    className={`flex-1 py-2 text-xs ${form.period_type === p.key ? "bg-maroon text-pearl" : "bg-white"} ${p.key !== "day" ? "border-l border-line" : ""}`}
                    data-testid={`target-type-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {form.period_type === "day" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Which day</span>
                <DateInput required className="mt-1" yearsBack={1} yearsAhead={2} value={form.period} onChange={(v) => setForm({...form, period: v})} data-testid="target-period-day"/>
              </label>
            )}
            {form.period_type === "week" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Which week</span>
                <input type="week" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} data-testid="target-period-week"/>
              </label>
            )}
            {form.period_type === "month" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Which month</span>
                <div className="flex gap-2 mt-1">
                  <select required className="border border-line px-2 py-2 bg-pearl text-sm flex-1" value={+(form.period.split("-")[1] || "")} onChange={(e) => setForm({...form, period: `${form.period.split("-")[0]}-${String(e.target.value).padStart(2,"0")}`})} data-testid="target-period-month">
                    {["January","February","March","April","May","June","July","August","September","October","November","December"].map((mn, i) => <option key={mn} value={i+1}>{mn}</option>)}
                  </select>
                  <select required className="border border-line px-2 py-2 bg-pearl text-sm w-[35%]" value={form.period.split("-")[0]} onChange={(e) => setForm({...form, period: `${e.target.value}-${form.period.split("-")[1] || "01"}`})}>
                    {[0,1,2].map((off) => { const yy = new Date().getFullYear() + off - 1; return <option key={yy} value={yy}>{yy}</option>; })}
                  </select>
                </div>
              </label>
            )}
            {form.period_type === "year" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Which year</span>
                <select required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} data-testid="target-period-year">
                  {[0,1,2].map((off) => { const yy = new Date().getFullYear() + off; return <option key={yy} value={String(yy)}>{yy}</option>; })}
                </select>
              </label>
            )}
            <input type="number" required placeholder="Target amount (₹)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.target_amount} onChange={(e) => setForm({...form, target_amount: +e.target.value})} data-testid="target-amount"/>
            <textarea placeholder="Notes" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="submit-target">Assign Target</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ============ EOD REPORT ============
export function EODReportPage() {
  const [form, setForm] = useState({ meetings: 0, trainings: 0, sales: 0, follow_up_calls: 0, inventory_checked: false, scheme_started: 0, target_status: "on-track", notes: "" });
  const [items, setItems] = useState([]);
  const [reporting, setReporting] = useState(null); // who I report to + the WA number the report goes to
  const load = () => api.get("/api/crm/eod-report/mine").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/api/crm/my-reporting").then((r) => setReporting(r.data)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api/crm/eod-report", form);
      toast.success("EOD report submitted");
      const summary = `EOD ${new Date().toLocaleDateString("en-GB")}\nMeetings: ${data.meetings}\nSales: ₹${data.sales}\nFollow-ups: ${data.follow_up_calls}\nSchemes: ${data.scheme_started}\nTarget: ${data.target_status}\n${data.notes || ""}`;
      // The report goes to the administrator's WhatsApp number that was set
      // while this salesperson was created (auto = single admin's phone).
      window.open(waLink(summary, reporting?.report_wa_number || ""), "_blank");
      // mark this report as shared to WhatsApp
      api.patch(`/api/crm/eod-report/${data.id}/mark-sent`).catch(() => {});
      setForm({ meetings: 0, trainings: 0, sales: 0, follow_up_calls: 0, inventory_checked: false, scheme_started: 0, target_status: "on-track", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-2 gap-6">
      <div>
        <div className="eyebrow">End of Day</div>
        <h1 className="font-display text-3xl text-maroon">Daily Workflow Report</h1>
        {reporting && (reporting.reporting_to || []).length > 0 && (
          <div className="bg-gold/10 border border-gold/40 p-3 mt-3 text-xs" data-testid="eod-reporting-info">
            <b>You report to:</b> {(reporting.reporting_to || []).map((a) => `${a.name} (${(a.role || "").replace("_", " ")})`).join(" · ")}
            {reporting.report_wa_number && <> — the report will be shared to WhatsApp <b>{reporting.report_wa_number}</b></>}
          </div>
        )}
        <form onSubmit={submit} className="bg-white border border-line p-6 mt-4 space-y-3">
          {[
            ["meetings", "Customer meetings", "number"],
            ["trainings", "Trainings attended", "number"],
            ["sales", "Sales (₹)", "number"],
            ["follow_up_calls", "Follow-up calls made", "number"],
            ["scheme_started", "Schemes started", "number"],
          ].map(([k, label, type]) => (
            <label key={k} className="block text-sm">
              <span className="eyebrow text-[10px]">{label}</span>
              <input type={type} className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form[k]} onChange={(e) => setForm({...form, [k]: type === "number" ? +e.target.value : e.target.value})} data-testid={`eod-${k}`}/>
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.inventory_checked} onChange={(e) => setForm({...form, inventory_checked: e.target.checked})} data-testid="eod-inventory"/> Inventory checked today</label>
          <select className="w-full border border-line px-3 py-2 bg-pearl" value={form.target_status} onChange={(e) => setForm({...form, target_status: e.target.value})} data-testid="eod-target">
            <option value="on-track">On track</option>
            <option value="behind">Behind</option>
            <option value="ahead">Ahead</option>
          </select>
          <textarea placeholder="Notes / blockers / reasons" rows={3} className="w-full border border-line px-3 py-2 bg-pearl" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <button className="btn-maroon w-full justify-center" data-testid="submit-eod">Submit + Share on WhatsApp</button>
        </form>
      </div>
      <div>
        <div className="eyebrow mb-3">My EOD statistics</div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            ["Reports filed", items.length],
            ["Shared on WhatsApp", items.filter((i) => i.wa_sent).length],
            ["Total meetings", items.reduce((a, i) => a + (i.meetings || 0), 0)],
          ].map(([l, v]) => (
            <div key={l} className="bg-white border border-line p-3 text-center">
              <div className="font-display text-2xl text-maroon">{v}</div>
              <div className="text-[9px] uppercase tracking-widest text-ash">{l}</div>
            </div>
          ))}
        </div>
        <div className="eyebrow mb-3">All my reports</div>
        <Paginated items={items} pageSize={15}>
          {(pageItems) => (
            <div className="space-y-2">
              {pageItems.map((it) => (
                <div key={it.id} className="bg-white border border-line p-3 text-sm" data-testid={`eod-${it.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{new Date(it.created_at).toLocaleDateString("en-GB")}</div>
                    {it.wa_sent
                      ? <span className="text-[9px] uppercase tracking-widest text-green-700 bg-green-50 px-1.5 py-0.5" data-testid={`eod-sent-${it.id}`}>Sent to WhatsApp</span>
                      : <span className="text-[9px] uppercase tracking-widest text-red-700 bg-red-50 px-1.5 py-0.5" data-testid={`eod-notsent-${it.id}`}>Not sent</span>}
                  </div>
                  <div className="text-xs text-ash mt-1">Meetings {it.meetings} · Sales {fmtINR(it.sales)} · Calls {it.follow_up_calls} · Schemes {it.scheme_started}</div>
                  {it.notes && <div className="text-xs mt-1">{it.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </Paginated>
        {items.length === 0 && <div className="text-center text-xs text-ash py-10">No EOD reports yet.</div>}
      </div>
    </div>
  );
}

// ============ SITE CMS (Banners + FAQ + Testimonials + Settings) ============
export function SiteCMSPage() {
  const [tab, setTab] = useState("banners");
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Storefront</div><h1 className="font-display text-4xl text-maroon">Site CMS</h1></div>
      <div className="flex border-b border-line mb-6">
        {["banners", "faqs", "testimonials", "reels", "settings"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm capitalize ${tab === t ? "border-b-2 border-maroon text-maroon font-semibold" : "text-ash"}`} data-testid={`cms-tab-${t}`}>{t}</button>
        ))}
      </div>
      {tab === "banners" && <BannersTab />}
      {tab === "faqs" && <FAQsTab />}
      {tab === "testimonials" && <TestimonialsTab />}
      {tab === "reels" && <ReelsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function BannersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/catalog", image_url: "", kind: "hero", active: true, sort_order: 0 });
  const [images, setImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/api/crm/banners").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const emptyBannerForm = { title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/catalog", image_url: "", kind: "hero", active: true, sort_order: 0 };
  const openCreate = () => { setEditingId(null); setForm(emptyBannerForm); setImages([]); setShowForm(true); };
  const openEdit = (b) => { setEditingId(b.id); setForm({ ...emptyBannerForm, ...b }); setImages(b.image_url ? [b.image_url] : []); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!images[0]) return toast.error("Please upload an image first — tap the Upload tile above");
    if (!form.title.trim()) return toast.error("Title is required");
    const imageUrl = typeof images[0] === "string" ? images[0] : (images[0]?.url || images[0]?.path || "");
    if (!imageUrl) return toast.error("Could not read the uploaded image URL — please re-upload");
    try {
      if (editingId) {
        // Update in place — this is the real "change the image" path:
        // uploading a new photo above replaces it without delete+recreate.
        await api.patch(`/api/crm/banners/${editingId}`, { ...form, image_url: imageUrl });
        toast.success("Banner updated");
      } else {
        await api.post("/api/crm/banners", { ...form, image_url: imageUrl });
        toast.success("Banner added");
      }
      setShowForm(false); setImages([]); setEditingId(null);
      setForm(emptyBannerForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the banner — please try again");
    }
  };
  const del = async (id) => { await api.delete(`/api/crm/banners/${id}`); load(); };
  return (
    <>
      <div className="flex justify-end mb-3"><button onClick={openCreate} className="btn-maroon" data-testid="add-banner-btn"><Plus size={14}/> New Banner</button></div>
      <div className="grid lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-line p-3" data-testid={`banner-${b.id}`}>
            <img src={b.image_url} alt="" className="w-full aspect-[16/9] object-cover"/>
            <div className="mt-2 font-medium text-sm">{b.title}</div>
            <div className="text-[10px] text-ash uppercase">{b.kind}{!b.active && " · INACTIVE"}</div>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => openEdit(b)} className="text-xs text-maroon underline" data-testid={`edit-banner-${b.id}`}>Edit</button>
              <button onClick={() => del(b.id)} className="text-xs text-red-600 underline" data-testid={`del-banner-${b.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <div className="font-display text-2xl text-maroon">{editingId ? "Edit Banner" : "New Banner"}</div>
            <ImageUploader value={images} onChange={setImages} multiple={false} kind="banner" maxFiles={1} />
            <input required placeholder="Title" className="w-full border border-line px-3 py-2 bg-pearl" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="banner-title"/>
            <input placeholder="Subtitle" className="w-full border border-line px-3 py-2 bg-pearl" value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})}/>
            <input placeholder="CTA text" className="w-full border border-line px-3 py-2 bg-pearl" value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})}/>
            <input placeholder="CTA link" className="w-full border border-line px-3 py-2 bg-pearl" value={form.cta_link} onChange={(e) => setForm({...form, cta_link: e.target.value})}/>
            <select className="w-full border border-line px-3 py-2 bg-pearl" value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})}>
              <option value="hero">Hero slider</option><option value="highlight">Homepage highlight card (Bridal/Daily Wear/Gifting row)</option><option value="seasonal">Seasonal</option><option value="partner">Partner</option>
            </select>
            <input type="number" placeholder="Sort order" className="w-full border border-line px-3 py-2 bg-pearl" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: +e.target.value})}/>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/> Active</label>
            <button className="btn-maroon w-full justify-center" data-testid="submit-banner">{editingId ? "Save Changes" : "Create"}</button>
          </form>
        </Modal>
      )}
    </>
  );
}

function ReelsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ youtube_url: "", title: "", sort_order: 0, active: true });
  const [igUrl, setIgUrl] = useState("");
  const load = () => {
    api.get("/api/crm/reels").then((r) => setItems(r.data || [])).catch(() => {});
    api.get("/api/reels").then((r) => setIgUrl(r.data?.instagram_url || "")).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.youtube_url.trim()) { toast.error("Paste a YouTube link"); return; }
    if (items.length >= 4) { toast.error("The home page shows 4 reels — delete one first"); return; }
    try { await api.post("/api/crm/reels", { ...form, sort_order: +form.sort_order || items.length }); setForm({ youtube_url: "", title: "", sort_order: 0, active: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this reel?")) return; await api.delete(`/api/crm/reels/${id}`); load(); };
  const toggle = async (r) => { await api.patch(`/api/crm/reels/${r.id}`, { active: !r.active }); load(); };
  const saveIg = async () => { try { await api.post("/api/crm/reels-instagram-url", { instagram_url: igUrl }); toast.success("Instagram link saved"); } catch { toast.error("Failed"); } };

  return (
    <div>
      <p className="text-xs text-ash mb-3">Add up to 4 YouTube links (Shorts or normal videos). They autoplay muted in a row on the home page. The "More reels on Instagram" button uses the link below.</p>

      {/* Instagram URL */}
      <div className="flex flex-wrap items-end gap-2 mb-5">
        <label className="text-sm flex-1 min-w-[240px]"><span className="text-[10px] text-ash uppercase tracking-widest">Instagram profile URL</span>
          <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={igUrl} onChange={(e) => setIgUrl(e.target.value)} placeholder="https://www.instagram.com/swayambhugold/" data-testid="reel-ig-url"/>
        </label>
        <button onClick={saveIg} className="btn-outline text-sm">Save link</button>
      </div>

      {/* Add reel */}
      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end mb-5">
        <label className="text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">YouTube link</span>
          <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/shorts/…" data-testid="reel-url"/>
        </label>
        <label className="text-sm"><span className="text-[10px] text-ash uppercase tracking-widest">Order</span>
          <input type="number" className="w-20 border border-line px-3 py-2 bg-pearl mt-1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}/>
        </label>
        <button onClick={add} className="btn-maroon" data-testid="reel-add"><Plus size={14}/> Add reel</button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-3 border border-line p-2.5" data-testid={`reel-row-${r.id}`}>
            <span className="text-[10px] text-ash w-6">#{r.sort_order}</span>
            <div className="flex-1 min-w-0"><div className="text-sm truncate">{r.title || r.youtube_url}</div><div className="text-[10px] text-ash truncate">{r.youtube_url}</div></div>
            <button onClick={() => toggle(r)} className={`text-xs px-2 py-1 rounded ${r.active ? "bg-green-100 text-green-800" : "bg-line text-ash"}`}>{r.active ? "Shown" : "Hidden"}</button>
            <button onClick={() => remove(r.id)} className="text-red-600"><X size={16}/></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-ash">No reels yet.</div>}
      </div>
    </div>
  );
}

function FAQsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", sort_order: 0, active: true });
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/api/crm/faqs").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); await api.post("/api/crm/faqs", form); toast.success("FAQ added"); setShowForm(false); setForm({ question: "", answer: "", sort_order: 0, active: true }); load(); };
  const del = async (id) => { await api.delete(`/api/crm/faqs/${id}`); load(); };
  return (
    <>
      <div className="flex justify-end mb-3"><button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-faq-btn"><Plus size={14}/> New FAQ</button></div>
      <div className="space-y-3">
        {items.map((q) => (
          <div key={q.id} className="bg-white border border-line p-4" data-testid={`faq-${q.id}`}>
            <div className="font-medium">{q.question}</div>
            <div className="text-sm text-ash mt-1">{q.answer}</div>
            <button onClick={() => del(q.id)} className="text-xs text-maroon underline mt-2">Delete</button>
          </div>
        ))}
      </div>
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={create} className="space-y-3">
            <div className="font-display text-2xl text-maroon">New FAQ</div>
            <input required placeholder="Question" className="w-full border border-line px-3 py-2 bg-pearl" value={form.question} onChange={(e) => setForm({...form, question: e.target.value})} data-testid="faq-question"/>
            <textarea required placeholder="Answer" rows={4} className="w-full border border-line px-3 py-2 bg-pearl" value={form.answer} onChange={(e) => setForm({...form, answer: e.target.value})} data-testid="faq-answer"/>
            <input type="number" placeholder="Sort order" className="w-full border border-line px-3 py-2 bg-pearl" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: +e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="submit-faq">Create</button>
          </form>
        </Modal>
      )}
    </>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ customer_name: "", location: "", quote: "", rating: 5, active: true });
  const [images, setImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/api/crm/testimonials").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); await api.post("/api/crm/testimonials", { ...form, image_url: images[0] || "" }); toast.success("Saved"); setShowForm(false); setImages([]); load(); };
  const del = async (id) => { await api.delete(`/api/crm/testimonials/${id}`); load(); };
  return (
    <>
      <div className="flex justify-end mb-3"><button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-test-btn"><Plus size={14}/> New Testimonial</button></div>
      <div className="grid lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-line p-4" data-testid={`testimonial-${t.id}`}>
            {t.image_url && <img src={t.image_url} alt="" className="w-12 h-12 rounded-full object-cover mb-3"/>}
            <div className="text-sm italic">"{t.quote}"</div>
            <div className="text-xs text-ash mt-2">— {t.customer_name}, {t.location}</div>
            <button onClick={() => del(t.id)} className="text-xs text-maroon underline mt-2">Delete</button>
          </div>
        ))}
      </div>
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={create} className="space-y-3">
            <div className="font-display text-2xl text-maroon">New Testimonial</div>
            <ImageUploader value={images} onChange={setImages} multiple={false} kind="testimonial" maxFiles={1} />
            <input required placeholder="Customer name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})}/>
            <input placeholder="Location" className="w-full border border-line px-3 py-2 bg-pearl" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}/>
            <textarea required placeholder="Quote" rows={4} className="w-full border border-line px-3 py-2 bg-pearl" value={form.quote} onChange={(e) => setForm({...form, quote: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="submit-test">Create</button>
          </form>
        </Modal>
      )}
    </>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({ showroom_video_url: "", delivery_fee: 250, free_delivery_above: 25000, astrology_premium_price: 99, astrology_free_words: 20, astrology_free_chars: 60, astrology_premium_words: 60, astrology_photo_price: 99, exchange_gst_percent: 3, exchange_deduction_per_10g: 1500, counter_payment_enabled: true, lock_rate_token_amount: 500, lock_rate_validity_days: 7 });
  useEffect(() => {
    api.get("/api/site-settings").then((r) => r.data && setForm({ ...form, ...r.data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async (e) => { e.preventDefault(); await api.post("/api/crm/site-settings", form); toast.success("Settings saved"); };
  return (
    <form onSubmit={save} className="bg-white border border-line p-6 max-w-2xl space-y-3">
      <label className="block text-sm">
        <span className="eyebrow text-[10px]">Showroom Video URL (YouTube embed link)</span>
        <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.showroom_video_url} onChange={(e) => setForm({...form, showroom_video_url: e.target.value})} data-testid="settings-video"/>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm"><span className="eyebrow text-[10px]">Delivery Fee (₹)</span><input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.delivery_fee} onChange={(e) => setForm({...form, delivery_fee: +e.target.value})} data-testid="settings-fee"/></label>
        <label className="text-sm"><span className="eyebrow text-[10px]">Free delivery above (₹)</span><input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.free_delivery_above} onChange={(e) => setForm({...form, free_delivery_above: +e.target.value})} data-testid="settings-free"/></label>
        <div className="sm:col-span-2 text-[11px] text-ash border-t border-line pt-3">
          Astrology chat pricing has moved to <b>Payouts → Astrology Pricing</b> (Super Admin / Major Admin only).
        </div>
        <label className="text-sm"><span className="eyebrow text-[10px]">Exchange GST %</span><input type="number" step="0.01" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.exchange_gst_percent} onChange={(e) => setForm({...form, exchange_gst_percent: +e.target.value})} data-testid="settings-exch-gst"/></label>
        <label className="text-sm"><span className="eyebrow text-[10px]">Exchange deduction (₹ / 10g)</span><input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.exchange_deduction_per_10g} onChange={(e) => setForm({...form, exchange_deduction_per_10g: +e.target.value})} data-testid="settings-exch-ded"/></label>
        <label className="text-sm"><span className="eyebrow text-[10px]">Lock-rate token (₹)</span><input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.lock_rate_token_amount} onChange={(e) => setForm({...form, lock_rate_token_amount: +e.target.value})} data-testid="settings-lock"/></label>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.counter_payment_enabled} onChange={(e) => setForm({...form, counter_payment_enabled: e.target.checked})} data-testid="settings-counter"/> Allow counter payment at checkout</label>
      <button className="btn-maroon" data-testid="save-settings">Save Settings</button>
    </form>
  );
}

// ============ COUPONS + WA TEMPLATES + FESTIVAL CRM ============
export function CouponsPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyCoupon = { code: "", discount_pct: 10, discount_amount: 0, min_order: 0, scope: "all", category: "", product_id: "", valid_from: "", expires_at: "", max_uses: 0, once_per_user: false, active: true };
  const [form, setForm] = useState(emptyCoupon);
  const load = () => api.get("/api/crm/coupons").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyCoupon); setShowForm(true); };
  const openEdit = (c) => { setEditingId(c.id); setForm({ ...emptyCoupon, ...c }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.patch(`/api/crm/coupons/${editingId}`, form); toast.success("Coupon updated"); }
      else { await api.post("/api/crm/coupons", { ...form, code: form.code.toUpperCase() }); toast.success("Coupon created"); }
      setShowForm(false); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/api/crm/coupons/${c.id}`); toast.success("Deleted"); load();
  };

  const scopeLabel = (c) => c.scope === "product" ? `Product ${(c.product_id || "").slice(0,8).toUpperCase()}` : c.scope === "category" ? c.category : "All products";

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Promo</div><h1 className="font-display text-4xl text-maroon">Coupons</h1></div>
        <button onClick={openCreate} className="btn-maroon" data-testid="add-coupon-btn"><Plus size={14}/> New Coupon</button>
      </div>
      <Paginated items={items} pageSize={15}>
        {(pageItems) => (
          <div className="grid lg:grid-cols-3 gap-4">
            {pageItems.map((c) => (
              <div key={c.id} className="bg-white border border-line p-5" data-testid={`coupon-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="font-display text-2xl text-gold-dark">{c.code}</div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(c)} className="text-maroon text-xs underline" data-testid={`edit-coupon-${c.id}`}>Edit</button>
                    <button onClick={() => remove(c)} className="text-red-600 text-xs underline" data-testid={`delete-coupon-${c.id}`}>Delete</button>
                  </div>
                </div>
                <div className="text-sm mt-1">{c.discount_pct ? `${c.discount_pct}% off` : fmtINR(c.discount_amount) + " off"} · <span className="text-ash">{scopeLabel(c)}</span></div>
                {(c.valid_from || c.expires_at) && <div className="text-xs text-ash mt-1">Valid {c.valid_from || "now"} → {c.expires_at || "no expiry"}</div>}
                <div className="text-xs text-ash mt-1">
                  Used {c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : " times"} {c.once_per_user && "· once per user"}
                </div>
                {c.min_order > 0 && <div className="text-xs text-ash">Min order: {fmtINR(c.min_order)}</div>}
              </div>
            ))}
          </div>
        )}
      </Paginated>
      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3 max-h-[80vh] overflow-y-auto pr-1">
            <div className="font-display text-2xl text-maroon">{editingId ? "Edit coupon" : "New coupon"}</div>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Coupon code (what the customer types)</span>
              <input required placeholder="e.g. DIWALI30" className="w-full border border-line px-3 py-2 bg-pearl uppercase mt-1" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} data-testid="coupon-code"/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm"><span className="eyebrow text-[10px]">Discount % (e.g. 30 = 30% off)</span>
                <input type="number" placeholder="Percentage off" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: +e.target.value})} data-testid="coupon-pct"/>
              </label>
              <label className="text-sm"><span className="eyebrow text-[10px]">OR flat discount ₹ (used if % is 0)</span>
                <input type="number" placeholder="Flat amount off" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.discount_amount} onChange={(e) => setForm({...form, discount_amount: +e.target.value})} data-testid="coupon-flat"/>
              </label>
            </div>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Minimum order value ₹ (0 = no minimum)</span>
              <input type="number" placeholder="Minimum cart value to redeem" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.min_order} onChange={(e) => setForm({...form, min_order: +e.target.value})} data-testid="coupon-min"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Applies to</span>
              <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.scope} onChange={(e) => setForm({...form, scope: e.target.value})} data-testid="coupon-scope">
                <option value="all">All products</option>
                <option value="category">A specific category (e.g. 30% off Necklaces)</option>
                <option value="product">One specific product (by Product ID)</option>
              </select>
            </label>
            {form.scope === "category" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Category</span>
                <select required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="coupon-category">
                  <option value="">Select category…</option>
                  {["Necklaces","Rings","Earrings","Bangles","Chains","Pendants","Bracelets","Mangalsutra","Nose Pins","Anklets","Silver Articles"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
            )}
            {form.scope === "product" && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Product ID (e.g. 2341ab…)</span>
                <input required placeholder="Paste the product ID this coupon is for" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.product_id} onChange={(e) => setForm({...form, product_id: e.target.value})} data-testid="coupon-product"/>
              </label>
            )}
            <label className="block text-sm"><span className="eyebrow text-[10px]">Redeemable FROM date</span>
              <DateInput className="mt-1" yearsBack={0} yearsAhead={3} value={form.valid_from} onChange={(v) => setForm({...form, valid_from: v})} data-testid="coupon-from"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Redeemable UNTIL date</span>
              <DateInput className="mt-1" yearsBack={0} yearsAhead={3} value={form.expires_at} onChange={(v) => setForm({...form, expires_at: v})} data-testid="coupon-to"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Total usage limit (how many times it can be redeemed · 0 = unlimited)</span>
              <input type="number" placeholder="e.g. 1 for a one-time 90% coupon" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: +e.target.value})} data-testid="coupon-maxuses"/>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.once_per_user} onChange={(e) => setForm({...form, once_per_user: e.target.checked})} data-testid="coupon-onceper"/>
              Each user can redeem this coupon only once
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/>
              Active
            </label>
            <button className="btn-maroon w-full justify-center" data-testid="submit-coupon">{editingId ? "Save Changes" : "Create Coupon"}</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function WATemplatesPage() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [form, setForm] = useState({ name: "", body: "", category: "general" });
  const [phForm, setPhForm] = useState({ key: "", label: "" });
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/api/crm/wa-templates").then((r) => setItems(r.data || []));
  const loadPh = () => api.get("/api/crm/wa-placeholders").then((r) => setPlaceholders(r.data || []));
  useEffect(() => { load(); loadPh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/api/crm/wa-templates", form); toast.success("Template saved"); setShowForm(false); setForm({ name: "", body: "", category: "general" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeTpl = async (t) => { if (!window.confirm(`Delete template "${t.name}"?`)) return; await api.delete(`/api/crm/wa-templates/${t.id}`); load(); };

  const addPlaceholder = async (e) => {
    e.preventDefault();
    try { await api.post("/api/crm/wa-placeholders", phForm); toast.success("Placeholder created"); setPhForm({ key: "", label: "" }); loadPh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removePh = async (p) => { await api.delete(`/api/crm/wa-placeholders/${p.id}`); loadPh(); };
  const insertPh = (key) => setForm((f) => ({ ...f, body: `${f.body}{{${key}}}` }));

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Outreach</div><h1 className="font-display text-4xl text-maroon">WhatsApp Templates</h1></div>
        <button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-watpl-btn"><Plus size={14}/> New Template</button>
      </div>

      {/* Placeholder manager — any placeholder can be created */}
      <div className="bg-white border border-line p-5 mb-6">
        <div className="eyebrow mb-2">Placeholders</div>
        <p className="text-xs text-ash mb-3">Built-in placeholders cover name, phone, LTV, scheme, recent purchase, coupon code, referral code (unique per customer), birthday, anniversary, festival, transaction ID, product, product ID, HUID, date and amount. Create any new placeholder below.</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {placeholders.map((p) => (
            <span key={p.key} className="inline-flex items-center gap-1 border border-line px-2 py-0.5 text-[11px] bg-pearl" title={p.label} data-testid={`ph-${p.key}`}>
              {`{{${p.key}}}`}
              {!p.builtin && <button onClick={() => removePh(p)} className="text-ash hover:text-red-600" data-testid={`ph-del-${p.key}`}><X size={10}/></button>}
            </span>
          ))}
        </div>
        <form onSubmit={addPlaceholder} className="flex flex-wrap gap-2">
          <input required placeholder="New placeholder key (e.g. gold_rate)" className="border border-line px-3 py-2 bg-pearl text-sm flex-1 min-w-[160px]" value={phForm.key} onChange={(e) => setPhForm({...phForm, key: e.target.value})} data-testid="ph-key"/>
          <input required placeholder="What it means (label)" className="border border-line px-3 py-2 bg-pearl text-sm flex-1 min-w-[160px]" value={phForm.label} onChange={(e) => setPhForm({...phForm, label: e.target.value})} data-testid="ph-label"/>
          <button className="btn-outline text-xs" data-testid="ph-add">Create Placeholder</button>
        </form>
      </div>

      <Paginated items={items} pageSize={15}>
        {(pageItems) => (
          <div className="grid lg:grid-cols-3 gap-4">
            {pageItems.map((t) => (
              <div key={t.id} className="bg-white border border-line p-5" data-testid={`watpl-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="font-medium">{t.name}</div>
                  <button onClick={() => removeTpl(t)} className="text-red-600 text-xs underline">Delete</button>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold-dark mt-0.5">{t.category}</div>
                <p className="text-xs text-ash mt-2 whitespace-pre-wrap">{t.body}</p>
              </div>
            ))}
          </div>
        )}
      </Paginated>
      {items.length === 0 && <div className="text-center text-ash text-sm p-10">No templates yet.</div>}

      {showForm && (
        <Modal close={() => setShowForm(false)}>
          <form onSubmit={create} className="space-y-3">
            <div className="font-display text-2xl text-maroon">New WhatsApp template</div>
            <input required placeholder="Template name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="watpl-name"/>
            <select className="w-full border border-line px-3 py-2 bg-pearl" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="watpl-category">
              {["general","festival","birthday","anniversary","scheme","winback","abandoned-cart"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <div>
              <div className="text-[10px] text-ash mb-1">Tap a placeholder to insert it:</div>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {placeholders.map((p) => (
                  <button type="button" key={p.key} onClick={() => insertPh(p.key)} className="border border-line px-1.5 py-0.5 text-[10px] bg-pearl hover:border-maroon" title={p.label} data-testid={`insert-${p.key}`}>
                    {`{{${p.key}}}`}
                  </button>
                ))}
              </div>
            </div>
            <textarea required rows={5} placeholder="Message body — e.g. Dear {{name}}, your referral code {{referral_code}} …" className="w-full border border-line px-3 py-2 bg-pearl" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="watpl-body"/>
            <button className="btn-maroon w-full justify-center" data-testid="submit-watpl">Save Template</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function FestivalCRMPage() {
  const [data, setData] = useState({ birthdays: [], anniversaries: [], templates: [] });
  useEffect(() => { api.get("/api/crm/festival/today").then((r) => setData(r.data)); }, []);
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Today</div><h1 className="font-display text-4xl text-maroon">Festival / Birthday / Anniversary CRM</h1></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <section>
          <div className="eyebrow mb-3">Birthdays today ({data.birthdays?.length})</div>
          <div className="space-y-2">
            <Paginated items={data.birthdays || []} pageSize={15}>
              {(pageItems) => pageItems.map((b) => (
                <div key={b.customer.id} className="bg-white border border-line p-3 flex items-center justify-between mb-2" data-testid={`bday-${b.customer.id}`}>
                  <div>
                    <div className="font-medium">{b.customer.name}</div>
                    <div className="text-xs text-ash">{b.customer.phone}</div>
                  </div>
                  <WASendButton phone={b.customer.phone} customerName={b.customer.name} context="birthday" waHref={b.wa_link}/>
                </div>
              ))}
            </Paginated>
            {data.birthdays?.length === 0 && <div className="text-xs text-ash">No birthdays today.</div>}
          </div>
        </section>
        <section>
          <div className="eyebrow mb-3">Anniversaries today ({data.anniversaries?.length})</div>
          <div className="space-y-2">
            <Paginated items={data.anniversaries || []} pageSize={15}>
              {(pageItems) => pageItems.map((b) => (
                <div key={b.customer.id} className="bg-white border border-line p-3 flex items-center justify-between mb-2" data-testid={`anniv-${b.customer.id}`}>
                  <div>
                    <div className="font-medium">{b.customer.name}</div>
                    <div className="text-xs text-ash">{b.customer.phone}</div>
                  </div>
                  <WASendButton phone={b.customer.phone} customerName={b.customer.name} context="anniversary" waHref={b.wa_link}/>
                </div>
              ))}
            </Paginated>
            {data.anniversaries?.length === 0 && <div className="text-xs text-ash">No anniversaries today.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

// ============ PREDICTIVE CRM ============
export function PredictivePage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/api/crm/predictive/customers").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">AI-driven</div><h1 className="font-display text-4xl text-maroon">Predictive CRM</h1>
      <p className="text-xs text-ash mt-1">Customers ripe for upsell — rule-based: high LTV + 60-180 day gap since last purchase.</p></div>
      <Paginated items={items} pageSize={15}>
        {(pageItems) => (
          <div className="bg-white border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-pearl border-b border-line"><tr>{["Customer", "Phone", "LTV", "Last Buy", "Score", "Suggested Action", "Reach Out"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
              <tbody>
                {pageItems.map((c) => (
                  <tr key={c.id} className="border-b border-line/60" data-testid={`pred-${c.id}`}>
                    <td className="p-3">{c.name}</td>
                    <td className="p-3 text-ash text-xs">{c.phone}</td>
                    <td className="p-3 text-maroon font-semibold">{fmtINR(c.ltv)}</td>
                    <td className="p-3 text-xs">{c.last_purchase_at?.slice(0,10)}</td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest ${c.score === "HOT" ? "text-red-700" : c.score === "WARM" ? "text-gold-dark" : "text-ash"}`}>{c.score}</span></td>
                    <td className="p-3 text-xs">{c.action}</td>
                    <td className="p-3"><WASendButton phone={c.phone} customerName={c.name} context="predictive" waHref={waLink(`Namaste ${c.name}! ${c.action || "We have curated new pieces you will love."} — Swayambhu Gold`, c.phone)}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No prospects identified yet.</div>}
          </div>
        )}
      </Paginated>
    </div>
  );
}

// ============ DASHBOARD PERIODS + Deadstock ============
export function DashboardPeriodPage() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [deadstock, setDeadstock] = useState([]);
  useEffect(() => {
    api.get(`/api/crm/dashboard/period?period=${period}`).then((r) => setData(r.data));
    api.get("/api/crm/inventory/deadstock?days=90").then((r) => setDeadstock(r.data || []));
  }, [period]);
  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Period</div><h1 className="font-display text-4xl text-maroon">Sales Insights</h1></div>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="period-select">
          <option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option>
          <option value="quarter">Quarterly</option><option value="halfyear">Half-Yearly</option><option value="year">Yearly</option>
        </select>
      </div>
      {data && (
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <KPI label={`Revenue (${period})`} value={fmtINR(data.revenue)} />
          <KPI label={`Orders (${period})`} value={data.orders_count} />
          <KPI label="Deadstock items" value={deadstock.length} warn />
        </div>
      )}
      {data?.by_category?.length > 0 && (
        <div className="bg-white border border-line p-5">
          <div className="eyebrow mb-4">Sales by Category</div>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={data.by_category} dataKey="value" nameKey="name" outerRadius={120} label>
                {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      {deadstock.length > 0 && (
        <div className="bg-white border border-line p-5 mt-6">
          <div className="eyebrow mb-3 flex items-center gap-2"><AlertTriangle size={14} className="text-gold-dark"/> Deadstock alert · {deadstock.length} items unsold &gt; 90 days</div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {deadstock.slice(0, 20).map((p) => (
              <div key={p.id} className="flex items-center gap-3 text-sm" data-testid={`dead-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover bg-pearl"/>
                <div className="flex-1">{p.name} <span className="text-ash text-xs">· {p.category} · {p.weight_grams}g</span></div>
                <div className="text-xs text-ash">{p.created_at?.slice(0,10)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, warn }) {
  return (
    <div className={`bg-white border ${warn ? "border-gold-dark" : "border-line"} p-5`} data-testid={`kpi-${label}`}>
      <div className="eyebrow text-[10px]">{label}</div>
      <div className="font-display text-3xl text-maroon mt-2">{value}</div>
    </div>
  );
}

// ============ DATA MIGRATION ============
export function DataMigrationPage() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState([]);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/api/crm/migrate/history").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/api/crm/migrate/products/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "swayambhu-products-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download the template"); }
  };

  const upload = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setErrors([]);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/api/crm/migrate/${kind}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      const bits = [`${data.imported} added`];
      if (data.updated) bits.push(`${data.updated} updated`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      setResult(`${kind}: ${bits.join(" · ")}`);
      setErrors(data.errors || []);
      if (data.skipped) toast.warning(`${bits.join(" · ")} — check the skipped rows below`);
      else toast.success(bits.join(" · "));
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Bulk Tools</div><h1 className="font-display text-4xl text-maroon">Data Migration (CSV)</h1></div>
      <p className="text-xs text-ash mb-4 max-w-2xl">
        Product uploads now live in <b>Inventory Ledger → Upload</b> — upload the label report there and each
        piece is added to live inventory and shown on the storefront. This page is just for bulk customer import.
      </p>
      <div className="grid lg:grid-cols-2 gap-4">
        {[
          { kind: "customers", headers: "name,phone,email,birthday,anniversary,location,notes,ltv,orders_count,loyalty_points" },
        ].map((b) => (
          <div key={b.kind} className="bg-white border border-line p-5">
            <div className="font-display text-2xl text-maroon capitalize">{b.kind}</div>
            <div className="text-xs text-ash mt-1">CSV headers: <code className="block text-[10px] mt-1 break-all">{b.headers}</code></div>
            {b.kind === "products" && (
              <p className="text-[11px] text-ash mt-2">
                Rows are matched on <b>barcode</b> — re-uploading a corrected sheet updates the same
                pieces instead of creating duplicates. Everything uploaded here shows
                "<b>Bulk Upload</b>" under Last Updated until someone edits it in-house.
                <br/>You can also upload the <b>Live Inventory label report</b> (the same .xls / CSV used in Inventory Ledger → Upload) here — each piece is added to live inventory and shown on the storefront.
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <label className="btn-maroon inline-flex cursor-pointer" data-testid={`migrate-${b.kind}`}>
                <input type="file" accept={b.kind === "products" ? ".csv,.xls,.xlsx" : ".csv"} className="hidden" onChange={(e) => upload(e, b.kind)} disabled={busy}/>
                {busy ? "Importing…" : "Upload CSV"}
              </label>
              {b.kind === "products" && (
                <button onClick={downloadTemplate} className="btn-outline text-sm" data-testid="download-product-template">
                  Download Template
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {result && <div className="mt-4 text-sm text-maroon" data-testid="migrate-result">{result}</div>}
      {errors.length > 0 && (
        <div className="mt-3 bg-white border border-line p-4" data-testid="migrate-errors">
          <div className="eyebrow mb-2 text-[10px]">Skipped rows</div>
          <ul className="text-xs text-ash space-y-1 max-h-56 overflow-y-auto">
            {errors.map((er, i) => <li key={i}>• {er}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-8">
        <div className="eyebrow mb-3">Import History</div>
        <Paginated items={history} pageSize={15}>
          {(pageItems) => (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line"><tr>{["When", "Type", "File", "Rows Imported", "By"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
                <tbody>
                  {pageItems.map((h) => (
                    <tr key={h.id} className="border-b border-line/60" data-testid={`mig-${h.id}`}>
                      <td className="p-3 text-xs">{new Date(h.at).toLocaleString()}</td>
                      <td className="p-3 capitalize">{h.kind}</td>
                      <td className="p-3 text-xs text-ash">{h.file}</td>
                      <td className="p-3">{h.imported}</td>
                      <td className="p-3 text-xs">{h.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {history.length === 0 && <div className="p-8 text-center text-ash text-sm">No imports yet.</div>}
            </div>
          )}
        </Paginated>
      </div>
    </div>
  );
}

// ============ BUDGET MATCHER (CRM tool) ============
export function BudgetMatcherCRMPage() {
  const [data, setData] = useState(null);
  const [lastQ, setLastQ] = useState(null);
  // Match by price OR by grams, per metal — same premium banner as the storefront
  const search = async ({ mode, value, metal }) => {
    setLastQ({ mode, value, metal });
    const qp = new URLSearchParams();
    qp.set(mode === "price" ? "budget" : "grams", value);
    if (metal) qp.set("metal", metal);
    try {
      const { data } = await api.get(`/api/budget-matcher?${qp.toString()}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
  };
  return (
    <div>
      <BudgetMatcherBar compact onMatch={search} />
      <div className="p-6 lg:p-8">
      {data && (
        <div>
          <div className="eyebrow mb-3">
            Matched {data.total_matched} pieces {lastQ?.mode === "price" ? `within ${fmtINR(lastQ.value)}` : `around ${lastQ?.value}g`} in {lastQ?.metal}
          </div>
          {Object.entries(data.groups).map(([cat, items]) => (
            <div key={cat} className="mb-6">
              <div className="font-display text-2xl text-maroon mb-3">{cat} <span className="text-xs text-ash">({items.length})</span></div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {items.slice(0, 8).map((p) => (
                  <div key={p.id} className="bg-white border border-line p-2" data-testid={`bm-${p.id}`}>
                    <img src={p.images?.[0]} alt="" className="w-full aspect-square object-cover bg-pearl"/>
                    <div className="text-xs mt-2 font-medium">{p.name}</div>
                    <div className="text-xs text-maroon font-semibold">{fmtINR(p.calculated_price)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

// ============ INVENTORY LEDGER ============
export function InventoryLedgerPage() {
  const [items, setItems] = useState([]);
  const pg = usePagination(items, 15);
  useEffect(() => { api.get("/api/crm/inventory/ledger").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Multi-store</div><h1 className="font-display text-4xl text-maroon">Inventory Movement Ledger</h1></div>
      <div className="bg-white border border-line">
        <Pagination {...pg} label="movements" compact/>
        <TableScroller>
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["When", "", "Product", "Weight", "HUID", "From", "To", "Qty", "Sent Through", "Requested By", "Approved By", "Note"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {pg.pageItems.map((it) => (
              <tr key={it.id} className="border-b border-line/60" data-testid={`inv-${it.id}`}>
                <td className="p-3 text-xs">{new Date(it.created_at).toLocaleString()}</td>
                <td className="p-2">
                  {it.product_image
                    ? <img src={it.product_image} alt="" className="w-10 h-10 object-cover bg-pearl border border-line"/>
                    : <div className="w-10 h-10 bg-pearl border border-line"/>}
                </td>
                <td className="p-3">
                  <div className="text-xs font-medium">{it.product_name || it.product_id?.slice(0,8)}</div>
                  <div className="text-[10px] text-ash">{it.metal} {it.purity}</div>
                </td>
                <td className="p-3 text-xs">{it.weight_grams != null ? `${it.weight_grams}g` : "—"}</td>
                <td className="p-3 text-xs">{it.huid || "—"}</td>
                <td className="p-3 text-xs">{storeLabel(it.from_store)}</td>
                <td className="p-3 text-xs">{storeLabel(it.to_store)}</td>
                <td className="p-3">{it.qty}</td>
                <td className="p-3 text-xs">{it.sent_through || "—"}</td>
                <td className="p-3 text-xs">{it.requested_by || "—"}</td>
                <td className="p-3 text-xs">{it.approved_by || "—"}</td>
                <td className="p-3 text-xs text-ash">{it.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroller>
        <Pagination {...pg} label="movements"/>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No movement yet.</div>}
      </div>
    </div>
  );
}

// ============ Modal helper ============
function Modal({ children, close }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={close}>
      <div className="bg-white border border-line p-6 w-full max-w-md relative my-10" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-3 right-3"><X size={18}/></button>
        {children}
      </div>
    </div>
  );
}
